import React, { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { stlLoader } from '../../../utils/stlLoader'

interface MarketplaceStlPreviewProps {
  loadStl: () => Promise<ArrayBuffer>
  fileName: string
}

export const MarketplaceStlPreview: React.FC<MarketplaceStlPreviewProps> = ({ loadStl, fileName }) => {
  const mountRef = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const mount = mountRef.current
    if (!mount) return

    let disposed = false
    const scene = new THREE.Scene()
    scene.background = new THREE.Color('#f8fafc')

    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / 320, 0.1, 1000)
    camera.position.set(0, 0, 18)

    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setSize(mount.clientWidth, 320)
    mount.innerHTML = ''
    mount.appendChild(renderer.domElement)

    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.autoRotate = true
    controls.autoRotateSpeed = 1.2

    scene.add(new THREE.AmbientLight(0xffffff, 1.1))
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.4)
    keyLight.position.set(6, 10, 12)
    scene.add(keyLight)

    let mesh: THREE.Mesh | null = null
    let frameId = 0
    const animate = () => {
      frameId = requestAnimationFrame(animate)
      controls.update()
      renderer.render(scene, camera)
    }

    const resize = () => {
      if (!mount) return
      camera.aspect = mount.clientWidth / 320
      camera.updateProjectionMatrix()
      renderer.setSize(mount.clientWidth, 320)
    }

    const run = async () => {
      try {
        const arrayBuffer = await loadStl()
        if (disposed) return

        const result = await stlLoader.loadSTLFromArrayBuffer(arrayBuffer)
        const geometry = result.geometry.clone()
        stlLoader.normalizeGeometry(geometry, 10)
        mesh = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: '#2563eb', metalness: 0.1, roughness: 0.65 }))
        scene.add(mesh)
        setIsLoading(false)
        animate()
      } catch (loadError) {
        if (disposed) return
        setError(loadError instanceof Error ? loadError.message : 'Could not load STL preview')
        setIsLoading(false)
      }
    }

    window.addEventListener('resize', resize)
    void run()

    return () => {
      disposed = true
      cancelAnimationFrame(frameId)
      window.removeEventListener('resize', resize)
      controls.dispose()
      mesh?.geometry.dispose()
      renderer.dispose()
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement)
      }
    }
  }, [loadStl])

  return (
    <div className="marketplace-stl-preview-card">
      <div className="marketplace-stl-preview-header">
        <h4>Shared STL Preview</h4>
        <span>{fileName}</span>
      </div>
      {isLoading && <div className="marketplace-stl-preview-state">Loading STL preview…</div>}
      {error && <div className="marketplace-stl-preview-state error">{error}</div>}
      <div ref={mountRef} className="marketplace-stl-preview-canvas" />
    </div>
  )
}